// Export Workflow ⇄ Classify tool round trip (UX redesign slice B).
//
// When a user has no Schedule B / HTS code, the Export Workflow sends them to the Classify tool
// in the SAME tab. Before leaving, the export chat writes this record to sessionStorage: a
// snapshot of the chat (the same shape sessionRecovery restores), the clf id the server keeps the
// workflow under, and the question still waiting for the code. The Classify page reads it to show
// its return banner (and writes `returnedCode` when the user clicks "Back to Export Workflow");
// the export chat consumes it on mount and restores the session.
//
// sessionStorage, not localStorage: tab-scoped, survives a refresh, no 15-minute draft cap. Capped
// at 4 h (the server's state-store idle TTL) and bound to the signed-in user (`sub`).
import { currentOwner } from 'services/sessionRecovery';

export const EXPORT_CLASSIFY_HANDOFF_KEY = 'bite.exportClassifyHandoff';
export const EXPORT_CLASSIFY_HANDOFF_MAX_AGE_MS = 4 * 60 * 60 * 1000;

export interface ExportClassifyHandoff<TSnapshot = unknown> {
  v: 1;
  owner: string | null;
  savedAt: number;
  clfId: string | null;
  snapshot: TSnapshot;
  productDescription: string;
  /** The follow-up question the returned code answers (handoff or not-found card). */
  pendingQuestion: string;
  /** Set by the Classify page's "Back to Export Workflow" once a code is final. */
  returnedCode?: string | null;
}

const read = (): ExportClassifyHandoff | null => {
  try {
    const raw = sessionStorage.getItem(EXPORT_CLASSIFY_HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || typeof parsed.savedAt !== 'number') return null;
    return parsed as ExportClassifyHandoff;
  } catch (error) {
    return null;
  }
};

const isUsable = (record: ExportClassifyHandoff | null): record is ExportClassifyHandoff => {
  if (!record) return false;
  if (Date.now() - record.savedAt > EXPORT_CLASSIFY_HANDOFF_MAX_AGE_MS) return false;
  // Both sides must be known to compare (same rule as sessionRecovery.loadDraft).
  const viewer = currentOwner();
  return !(record.owner && viewer && record.owner !== viewer);
};

export const saveExportClassifyHandoff = <TSnapshot>(
  record: Omit<ExportClassifyHandoff<TSnapshot>, 'v' | 'owner' | 'savedAt'>,
): boolean => {
  try {
    sessionStorage.setItem(
      EXPORT_CLASSIFY_HANDOFF_KEY,
      JSON.stringify({ ...record, v: 1, owner: currentOwner(), savedAt: Date.now() }),
    );
    return true;
  } catch (error) {
    // Storage full / blocked: the caller stays on the page rather than lose the session.
    console.warn('exportClassifyHandoff: failed to save', error);
    return false;
  }
};

/** The pending handoff (without consuming it), or null when absent / stale / someone else's. */
export const peekExportClassifyHandoff = <TSnapshot = unknown>():
  | ExportClassifyHandoff<TSnapshot>
  | null => {
  const record = read();
  if (!isUsable(record)) {
    if (record) clearExportClassifyHandoff();
    return null;
  }
  return record as ExportClassifyHandoff<TSnapshot>;
};

/** Reads and clears the handoff (the export chat restores from it exactly once). */
export const takeExportClassifyHandoff = <TSnapshot = unknown>():
  | ExportClassifyHandoff<TSnapshot>
  | null => {
  const record = peekExportClassifyHandoff<TSnapshot>();
  clearExportClassifyHandoff();
  return record;
};

export const setExportClassifyHandoffCode = (code: string): void => {
  const record = read();
  if (!record) return;
  try {
    sessionStorage.setItem(
      EXPORT_CLASSIFY_HANDOFF_KEY,
      JSON.stringify({ ...record, returnedCode: code }),
    );
  } catch (error) {
    console.warn('exportClassifyHandoff: failed to store the returned code', error);
  }
};

export const clearExportClassifyHandoff = (): void => {
  try {
    sessionStorage.removeItem(EXPORT_CLASSIFY_HANDOFF_KEY);
  } catch (error) {
    // ignore
  }
};

/**
 * Pull a 10-digit code out of free text ("8806.23.0000", "8806230000 – UAV"). Digit boundaries on
 * both sides, so a longer number is rejected instead of silently cut to its first ten digits.
 */
export const extractTenDigitCode = (text: string | null | undefined): string | null => {
  const match = String(text || '').match(/(?:^|\D)(\d{4}\.?\d{2}\.?\d{2}\.?\d{2})(?!\d)/);
  return match ? match[1] : null;
};

// Commodity-code shapes (mirrors pyapi agents/hts_managed_agent/code_hints.py): a dotted code, or a
// bare 8-10 digit run, optionally led by a code keyword ("HTS code 1234.56.7890", "Schedule B: …").
const CODE_KEYWORD = String.raw`(?:(?:hts(?:us)?|hs|schedule\s*b|tariff|commodity)\s*(?:code|number|no\.?)?|code|number)`;
const CODE_PHRASE_RE = new RegExp(
  String.raw`(?:\b${CODE_KEYWORD}\s*[:#-]?\s*)?["“”']?\b(?:\d{4}\.\d{2}(?:\.\d{2,4}){0,2}|\d{8,10})\b["“”']?`,
  'gi',
);

/**
 * The product description handed to the Classify tool, without any commodity code typed into it.
 * A code the export workflow rejected (E1) must not reach Classify, which reads a code in the
 * description as a hint and would anchor on the wrong chapter (PR review 2026-10-08).
 */
export const stripCommodityCodes = (text: string | null | undefined): string =>
  String(text || '')
    .replace(CODE_PHRASE_RE, ' ')
    .replace(/[ \t]*([,;:])(?:[ \t]*[,;:])+/g, '$1')
    .replace(/\(\s*\)/g, '')
    .replace(/["“”']\s*["“”']/g, '')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,;:-]+|[\s,;:-]+$/g, '')
    .trim();
